import {
	type CategoryStage,
	DAY_CLOSES_KEY,
	type DayClose,
	emptyHouseholdConsumption,
	type HouseholdConsumptionState,
	isRecurringPayment,
	type MemoryMood,
	type MemoryOccasion,
	normalizeShoppingEntryIds,
	type PaymentTraceSource,
	type PlainDate,
	type PurchaseReason,
	type PurchaseSatisfaction,
	parseDayCloses,
	type RepurchaseIntent,
} from "@sobidogam/domain";
import Dexie, { type Table } from "dexie";

export interface Receipt {
	receiptId: string;
	transactedAt: string;
	storeKey: string;
	storeDisplay: string;
	brandId: string | null;
	categoryId: string;
	totalAmount: number;
	currency: "KRW";
	source: PaymentTraceSource;
	categoryPinned: boolean;
	sensitiveMasked: boolean;
	memo: string | null;
	mood?: MemoryMood | null;
	occasion?: MemoryOccasion | null;
	isFavorite?: boolean;
	purchaseReason?: PurchaseReason | null;
	expectedScene?: string | null;
	satisfaction?: PurchaseSatisfaction | null;
	repurchaseIntent?: RepurchaseIntent | null;
	afterNote?: string | null;
	reviewedAt?: string | null;
	createdAt: string;
	updatedAt: string;
	deletedAt: string | null;
	/** A cancelled purchase is recoverable in the same trash flow as an accidental delete. */
	voidReason?: "refund" | null;
	syncState: "local" | "queued" | "synced";
	tagIds?: string[];
	items?: ReceiptLine[];
	recurringPaymentId?: string | null;
}

export interface ReceiptLine {
	name: string;
	amount: number | null;
	qty: number | null;
}

export function normalizeReceiptForStorage(receipt: Receipt): Receipt {
	if (!receipt.sensitiveMasked) {
		return {
			...receipt,
			items: (receipt.items ?? [])
				.map((item) => ({
					name: item.name
						.normalize("NFKC")
						.trim()
						.replace(/\s+/g, " ")
						.slice(0, 120),
					amount:
						item.amount === null ? null : Math.max(0, Math.round(item.amount)),
					qty: item.qty === null ? null : Math.max(0, item.qty),
				}))
				.filter((item) => item.name),
			mood: receipt.mood ?? null,
			occasion: receipt.occasion ?? null,
			isFavorite: receipt.isFavorite ?? false,
			purchaseReason: receipt.purchaseReason ?? null,
			expectedScene: receipt.expectedScene?.trim().slice(0, 160) || null,
			satisfaction: receipt.satisfaction ?? null,
			repurchaseIntent: receipt.repurchaseIntent ?? null,
			afterNote: receipt.afterNote?.trim().slice(0, 240) || null,
			reviewedAt: receipt.reviewedAt ?? null,
			voidReason: receipt.voidReason ?? null,
		};
	}
	return {
		...receipt,
		items: [],
		memo: null,
		mood: null,
		occasion: null,
		isFavorite: false,
		purchaseReason: null,
		expectedScene: null,
		satisfaction: null,
		repurchaseIntent: null,
		afterNote: null,
		reviewedAt: null,
		voidReason: receipt.voidReason ?? null,
	};
}

export const LOCAL_DATA_KEYS = {
	receipts: "sobidogam:receipts",
	household: "sobidogam:household",
	quarantinePrefix: "sobidogam:recovery:local-data:",
} as const;

export interface LocalStoragePort {
	readonly length: number;
	getItem(key: string): string | null;
	key(index: number): string | null;
	removeItem(key: string): void;
	setItem(key: string, value: string): void;
}

export type LocalDataIssueReason =
	| "read_error"
	| "invalid_json"
	| "invalid_schema";

export interface LocalDataIssue {
	source: "receipts" | "household";
	reason: LocalDataIssueReason;
}

export interface LocalDataRawSources {
	receipts: string | null;
	household: string | null;
}

export type LocalDataLoadResult =
	| {
			status: "ready";
			receipts: Receipt[];
			household: HouseholdConsumptionState;
			raw: LocalDataRawSources;
	  }
	| {
			status: "recovery";
			issues: LocalDataIssue[];
			raw: LocalDataRawSources;
			canQuarantine: boolean;
	  };

export interface LocalDataQuarantine {
	storageKey: string;
	id: string;
	createdAt: string;
	issues: LocalDataIssue[];
	sources: {
		receipts: {
			sourceKey: typeof LOCAL_DATA_KEYS.receipts;
			raw: string | null;
		};
		household: {
			sourceKey: typeof LOCAL_DATA_KEYS.household;
			raw: string | null;
		};
	};
}

const RECEIPT_SOURCES = new Set<PaymentTraceSource>([
	"ai",
	"manual",
	"notification",
	"screenshot",
	"csv",
]);

function isObject(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isReceipt(value: unknown): value is Receipt {
	if (!isObject(value)) return false;
	const items = value.items;
	return (
		typeof value.receiptId === "string" &&
		value.receiptId.length > 0 &&
		typeof value.transactedAt === "string" &&
		typeof value.storeKey === "string" &&
		typeof value.storeDisplay === "string" &&
		(value.brandId === null || typeof value.brandId === "string") &&
		typeof value.categoryId === "string" &&
		typeof value.totalAmount === "number" &&
		Number.isFinite(value.totalAmount) &&
		value.currency === "KRW" &&
		typeof value.source === "string" &&
		RECEIPT_SOURCES.has(value.source as PaymentTraceSource) &&
		typeof value.categoryPinned === "boolean" &&
		typeof value.sensitiveMasked === "boolean" &&
		(value.memo === null || typeof value.memo === "string") &&
		typeof value.createdAt === "string" &&
		typeof value.updatedAt === "string" &&
		(value.deletedAt === null || typeof value.deletedAt === "string") &&
		(value.voidReason === undefined ||
			value.voidReason === null ||
			value.voidReason === "refund") &&
		["local", "queued", "synced"].includes(String(value.syncState)) &&
		(items === undefined ||
			(Array.isArray(items) &&
				items.every(
					(item) =>
						isObject(item) &&
						typeof item.name === "string" &&
						(item.amount === null || typeof item.amount === "number") &&
						(item.qty === null || typeof item.qty === "number"),
				)))
	);
}

function isHouseholdConsumptionState(
	value: unknown,
): value is HouseholdConsumptionState {
	return (
		isObject(value) &&
		value.schemaVersion === 1 &&
		Array.isArray(value.items) &&
		Array.isArray(value.shopping) &&
		Array.isArray(value.budgetPlans) &&
		value.budgetPlans.every(
			(plan) =>
				isObject(plan) &&
				(plan.monthlySpendable === undefined ||
					(typeof plan.monthlySpendable === "number" &&
						Number.isSafeInteger(plan.monthlySpendable) &&
						plan.monthlySpendable > 0)),
		) &&
		(value.recurringPayments === undefined ||
			(Array.isArray(value.recurringPayments) &&
				value.recurringPayments.every(isRecurringPayment) &&
				new Set(value.recurringPayments.map((payment) => payment.id)).size ===
					value.recurringPayments.length))
	);
}

export function isLocalSnapshot(
	receipts: unknown,
	household: unknown,
): boolean {
	return (
		Array.isArray(receipts) &&
		receipts.every(isReceipt) &&
		new Set(receipts.map((receipt) => receipt.receiptId)).size ===
			receipts.length &&
		isHouseholdConsumptionState(household)
	);
}

function parseJson(raw: string): { ok: true; value: unknown } | { ok: false } {
	try {
		return { ok: true, value: JSON.parse(raw) };
	} catch {
		return { ok: false };
	}
}

export function loadLocalData(storage: LocalStoragePort): LocalDataLoadResult {
	const issues: LocalDataIssue[] = [];
	let receiptsRaw: string | null = null;
	let householdRaw: string | null = null;
	let receiptsReadable = true;
	let householdReadable = true;

	try {
		receiptsRaw = storage.getItem(LOCAL_DATA_KEYS.receipts);
	} catch {
		receiptsReadable = false;
		issues.push({ source: "receipts", reason: "read_error" });
	}
	try {
		householdRaw = storage.getItem(LOCAL_DATA_KEYS.household);
	} catch {
		householdReadable = false;
		issues.push({ source: "household", reason: "read_error" });
	}

	let receipts: Receipt[] = [];
	if (receiptsReadable && receiptsRaw !== null) {
		const parsed = parseJson(receiptsRaw);
		if (!parsed.ok) {
			issues.push({ source: "receipts", reason: "invalid_json" });
		} else if (!Array.isArray(parsed.value) || !parsed.value.every(isReceipt)) {
			issues.push({ source: "receipts", reason: "invalid_schema" });
		} else {
			receipts = parsed.value.map(normalizeReceiptForStorage);
		}
	}

	let household = emptyHouseholdConsumption();
	if (householdReadable && householdRaw !== null) {
		const parsed = parseJson(householdRaw);
		if (!parsed.ok) {
			issues.push({ source: "household", reason: "invalid_json" });
		} else if (!isHouseholdConsumptionState(parsed.value)) {
			issues.push({ source: "household", reason: "invalid_schema" });
		} else {
			household = normalizeShoppingEntryIds(parsed.value);
		}
	}

	const raw = { receipts: receiptsRaw, household: householdRaw };
	if (issues.length > 0) {
		return {
			status: "recovery",
			issues,
			raw,
			canQuarantine: receiptsReadable && householdReadable,
		};
	}
	return { status: "ready", receipts, household, raw };
}

function assertWritable(storage: LocalStoragePort): void {
	if (loadLocalData(storage).status === "recovery")
		throw new Error("E-LOCAL-RECOVERY-REQUIRED");
}

export function writeLocalReceipts(
	storage: LocalStoragePort,
	receipts: Receipt[],
): void {
	assertWritable(storage);
	const serialized = JSON.stringify(receipts.map(normalizeReceiptForStorage));
	storage.setItem(LOCAL_DATA_KEYS.receipts, serialized);
	if (storage.getItem(LOCAL_DATA_KEYS.receipts) !== serialized)
		throw new Error("E-LOCAL-WRITE-VERIFY");
}

export function writeLocalHousehold(
	storage: LocalStoragePort,
	household: HouseholdConsumptionState,
): void {
	assertWritable(storage);
	const serialized = JSON.stringify(household);
	storage.setItem(LOCAL_DATA_KEYS.household, serialized);
	if (storage.getItem(LOCAL_DATA_KEYS.household) !== serialized)
		throw new Error("E-LOCAL-WRITE-VERIFY");
}

function isLocalDataQuarantine(
	value: unknown,
): value is Omit<LocalDataQuarantine, "storageKey"> {
	if (!isObject(value) || !isObject(value.sources)) return false;
	const receipts = value.sources.receipts;
	const household = value.sources.household;
	return (
		value.schemaVersion === 1 &&
		typeof value.id === "string" &&
		typeof value.createdAt === "string" &&
		Array.isArray(value.issues) &&
		isObject(receipts) &&
		receipts.sourceKey === LOCAL_DATA_KEYS.receipts &&
		(receipts.raw === null || typeof receipts.raw === "string") &&
		isObject(household) &&
		household.sourceKey === LOCAL_DATA_KEYS.household &&
		(household.raw === null || typeof household.raw === "string")
	);
}

export function listLocalDataQuarantines(
	storage: LocalStoragePort,
): LocalDataQuarantine[] {
	const rows: LocalDataQuarantine[] = [];
	for (let index = 0; index < storage.length; index += 1) {
		const storageKey = storage.key(index);
		if (!storageKey?.startsWith(LOCAL_DATA_KEYS.quarantinePrefix)) continue;
		try {
			const raw = storage.getItem(storageKey);
			if (raw === null) continue;
			const parsed = JSON.parse(raw) as unknown;
			if (isLocalDataQuarantine(parsed)) rows.push({ ...parsed, storageKey });
		} catch {
			// A corrupt recovery copy is never promoted to a usable export.
		}
	}
	return rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function quarantineLocalData(
	storage: LocalStoragePort,
	result: Extract<LocalDataLoadResult, { status: "recovery" }>,
	options: { id?: string; createdAt?: string } = {},
): LocalDataQuarantine {
	if (!result.canQuarantine) throw new Error("E-LOCAL-RAW-UNREADABLE");
	const createdAt = options.createdAt ?? new Date().toISOString();
	const id =
		options.id ??
		`${createdAt.replace(/[^0-9]/g, "").slice(0, 17)}-${globalThis.crypto.randomUUID()}`;
	if (!/^[a-zA-Z0-9-]+$/.test(id)) throw new Error("E-LOCAL-QUARANTINE-ID");
	const storageKey = `${LOCAL_DATA_KEYS.quarantinePrefix}${id}`;
	const manifest = {
		schemaVersion: 1,
		id,
		createdAt,
		issues: result.issues,
		sources: {
			receipts: {
				sourceKey: LOCAL_DATA_KEYS.receipts,
				raw: result.raw.receipts,
			},
			household: {
				sourceKey: LOCAL_DATA_KEYS.household,
				raw: result.raw.household,
			},
		},
	};
	const serialized = JSON.stringify(manifest);
	storage.setItem(storageKey, serialized);
	if (storage.getItem(storageKey) !== serialized)
		throw new Error("E-LOCAL-QUARANTINE-VERIFY");
	storage.removeItem(LOCAL_DATA_KEYS.receipts);
	storage.removeItem(LOCAL_DATA_KEYS.household);
	if (
		storage.getItem(LOCAL_DATA_KEYS.receipts) !== null ||
		storage.getItem(LOCAL_DATA_KEYS.household) !== null
	)
		throw new Error("E-LOCAL-RESET-VERIFY");
	return { ...manifest, storageKey };
}

export function deleteLocalDataQuarantine(
	storage: LocalStoragePort,
	storageKey: string,
): void {
	if (!storageKey.startsWith(LOCAL_DATA_KEYS.quarantinePrefix))
		throw new Error("E-LOCAL-QUARANTINE-KEY");
	storage.removeItem(storageKey);
	if (storage.getItem(storageKey) !== null)
		throw new Error("E-LOCAL-QUARANTINE-DELETE");
}

export interface ReceiptItem {
	id?: number;
	receiptId: string;
	name: string;
	amount: number | null;
	qty: number | null;
}

export interface DogamCell {
	cellKey: string;
	kind: "category" | "brand" | "store";
	refId: string;
	stage: CategoryStage;
	visitCount: number;
	firstVisitAt: string | null;
	lastVisitAt: string | null;
}

export interface AppSetting {
	key: string;
	value: string;
}
export interface AppTag {
	tagId: string;
	name: string;
	color: string;
}
export interface ReceiptTag {
	receiptId: string;
	tagId: string;
}

export class SobidogamDb extends Dexie {
	receipts!: Table<Receipt, string>;
	items!: Table<ReceiptItem, number>;
	dogamCells!: Table<DogamCell, string>;
	settings!: Table<AppSetting, string>;
	tags!: Table<AppTag, string>;
	receiptTags!: Table<ReceiptTag, [string, string]>;

	constructor(name = "sobidogam") {
		super(name);
		this.version(1).stores({
			receipts:
				"&receiptId, transactedAt, categoryId, brandId, storeKey, syncState, deletedAt",
			items: "++id, receiptId, [receiptId+id]",
			dogamCells: "&cellKey, kind, stage, visitCount, lastVisitAt",
			settings: "&key",
			tags: "&tagId, name",
			receiptTags: "[receiptId+tagId], tagId",
		});
		this.version(2).stores({
			receipts:
				"&receiptId, transactedAt, categoryId, brandId, storeKey, syncState, deletedAt, isFavorite",
			items: "++id, receiptId, [receiptId+id]",
			dogamCells: "&cellKey, kind, stage, visitCount, lastVisitAt",
			settings: "&key",
			tags: "&tagId, name",
			receiptTags: "[receiptId+tagId], tagId",
		});
	}
}

export interface ReceiptStore {
	listReceipts(): Promise<Receipt[]>;
	listAllReceipts(): Promise<Receipt[]>;
	saveReceipt(receipt: Receipt, items?: ReceiptItem[]): Promise<void>;
	deleteReceipt(receiptId: string, deletedAt: string): Promise<void>;
	clearAll(): Promise<void>;
	replaceAll(receipts: Receipt[]): Promise<void>;
	listCells(): Promise<DogamCell[]>;
	saveCells(cells: DogamCell[]): Promise<void>;
	getSetting(key: string): Promise<string | null>;
	setSetting(key: string, value: string): Promise<void>;
}

export class DexieReceiptStore implements ReceiptStore {
	constructor(readonly db = new SobidogamDb()) {}
	listReceipts() {
		return this.db.receipts
			.filter((receipt) => receipt.deletedAt === null)
			.toArray()
			.then((receipts) => receipts.map(normalizeReceiptForStorage));
	}
	listAllReceipts() {
		return this.db.receipts
			.toArray()
			.then((receipts) => receipts.map(normalizeReceiptForStorage));
	}
	async saveReceipt(receipt: Receipt, items?: ReceiptItem[]) {
		await this.db.transaction(
			"rw",
			this.db.receipts,
			this.db.items,
			async () => {
				await this.db.receipts.put(normalizeReceiptForStorage(receipt));
				if (items !== undefined) {
					await this.db.items
						.where("receiptId")
						.equals(receipt.receiptId)
						.delete();
					if (items.length) await this.db.items.bulkAdd(items);
				}
			},
		);
	}
	async deleteReceipt(receiptId: string, deletedAt: string): Promise<void> {
		await this.db.receipts.update(receiptId, {
			deletedAt,
			syncState: "queued",
		});
	}
	async clearAll(): Promise<void> {
		await this.db.transaction(
			"rw",
			[
				this.db.receipts,
				this.db.items,
				this.db.dogamCells,
				this.db.settings,
				this.db.tags,
				this.db.receiptTags,
			],
			async () => {
				await this.db.receipts.clear();
				await this.db.items.clear();
				await this.db.dogamCells.clear();
				await this.db.settings.clear();
				await this.db.tags.clear();
				await this.db.receiptTags.clear();
			},
		);
	}
	async replaceAll(receipts: Receipt[]): Promise<void> {
		await this.db.transaction(
			"rw",
			[this.db.receipts, this.db.items, this.db.receiptTags],
			async () => {
				await this.db.items.clear();
				await this.db.receiptTags.clear();
				await this.db.receipts.clear();
				await this.db.receipts.bulkPut(
					receipts.map(normalizeReceiptForStorage),
				);
				await this.db.items.bulkAdd(
					receipts.flatMap((receipt) =>
						(normalizeReceiptForStorage(receipt).items ?? []).map((item) => ({
							receiptId: receipt.receiptId,
							...item,
						})),
					),
				);
				await this.db.receiptTags.bulkPut(
					receipts.flatMap((receipt) =>
						(receipt.tagIds ?? []).map((tagId) => ({
							receiptId: receipt.receiptId,
							tagId,
						})),
					),
				);
			},
		);
	}
	listCells() {
		return this.db.dogamCells.toArray();
	}
	async saveCells(cells: DogamCell[]): Promise<void> {
		await this.db.dogamCells.bulkPut(cells);
	}
	async getSetting(key: string) {
		return (await this.db.settings.get(key))?.value ?? null;
	}
	async setSetting(key: string, value: string): Promise<void> {
		await this.db.settings.put({ key, value });
	}
}

export class MemoryReceiptStore implements ReceiptStore {
	private receipts = new Map<string, Receipt>();
	private cells = new Map<string, DogamCell>();
	private settings = new Map<string, string>();
	listReceipts() {
		return Promise.resolve(
			[...this.receipts.values()]
				.filter((receipt) => receipt.deletedAt === null)
				.map(normalizeReceiptForStorage),
		);
	}
	listAllReceipts() {
		return Promise.resolve(
			[...this.receipts.values()].map(normalizeReceiptForStorage),
		);
	}
	saveReceipt(receipt: Receipt) {
		this.receipts.set(receipt.receiptId, normalizeReceiptForStorage(receipt));
		return Promise.resolve();
	}
	deleteReceipt(receiptId: string, deletedAt: string) {
		const receipt = this.receipts.get(receiptId);
		if (receipt) this.receipts.set(receiptId, { ...receipt, deletedAt });
		return Promise.resolve();
	}
	clearAll() {
		this.receipts.clear();
		this.cells.clear();
		this.settings.clear();
		return Promise.resolve();
	}
	replaceAll(receipts: Receipt[]) {
		this.receipts.clear();
		for (const receipt of receipts)
			this.receipts.set(receipt.receiptId, normalizeReceiptForStorage(receipt));
		return Promise.resolve();
	}
	listCells() {
		return Promise.resolve([...this.cells.values()]);
	}
	saveCells(cells: DogamCell[]) {
		cells.forEach((cell) => {
			this.cells.set(cell.cellKey, cell);
		});
		return Promise.resolve();
	}
	getSetting(key: string) {
		return Promise.resolve(this.settings.get(key) ?? null);
	}
	setSetting(key: string, value: string) {
		this.settings.set(key, value);
		return Promise.resolve();
	}
}

export class ReceiptHouseholdPersistError extends Error {
	constructor(
		readonly cause: unknown,
		readonly rollbackFailed = false,
	) {
		super("E-RECEIPT-HOUSEHOLD-PERSIST");
	}
}

/**
 * Keeps local-first mutations in call order even when IndexedDB acknowledgement
 * is delayed. A rejected operation does not poison the following retry.
 */
export function createSerialAsyncQueue() {
	let tail: Promise<void> = Promise.resolve();
	return {
		enqueue<T>(operation: () => Promise<T>): Promise<T> {
			const result = tail.then(operation, operation);
			tail = result.then(
				() => undefined,
				() => undefined,
			);
			return result;
		},
	};
}

function restoreLocalValue(
	storage: LocalStoragePort,
	key: string,
	value: string | null,
): void {
	if (value === null) storage.removeItem(key);
	else storage.setItem(key, value);
}

/**
 * Mirrors one receipt and its household consequence without changing visible
 * state until both local storage records and the receipt store agree.  Local
 * storage has no multi-key transaction, so a failed second write is explicitly
 * restored to the captured pair before the failure is returned to the caller.
 */
export async function persistReceiptAndHouseholdChange(input: {
	storage: LocalStoragePort;
	receiptStore: ReceiptStore;
	beforeReceipts: Receipt[];
	afterReceipts: Receipt[];
	beforeHousehold: HouseholdConsumptionState;
	afterHousehold: HouseholdConsumptionState;
	changedReceipt?: Receipt;
	/** A confirmed import is one visible change, including every saved row. */
	changedReceipts?: Receipt[];
	/** Used by backup replacement, including a valid empty backup. */
	replaceAll?: boolean;
}): Promise<void> {
	const previousReceipts = input.storage.getItem(LOCAL_DATA_KEYS.receipts);
	const previousHousehold = input.storage.getItem(LOCAL_DATA_KEYS.household);
	const changed =
		input.changedReceipts ??
		(input.changedReceipt ? [input.changedReceipt] : []);
	try {
		writeLocalReceipts(input.storage, input.afterReceipts);
		writeLocalHousehold(input.storage, input.afterHousehold);
		if (input.replaceAll)
			await input.receiptStore.replaceAll(input.afterReceipts);
		else
			for (const receipt of changed)
				await input.receiptStore.saveReceipt(
					receipt,
					(normalizeReceiptForStorage(receipt).items ?? []).map((item) => ({
						receiptId: receipt.receiptId,
						...item,
					})),
				);
		const persisted = await input.receiptStore.listAllReceipts();
		if (
			(input.replaceAll && persisted.length !== input.afterReceipts.length) ||
			(input.replaceAll ? input.afterReceipts : changed).some(
				(receipt) =>
					JSON.stringify(
						persisted.find((stored) => stored.receiptId === receipt.receiptId),
					) !== JSON.stringify(normalizeReceiptForStorage(receipt)),
			)
		) {
			throw new Error("E-RECEIPT-STORE-WRITE-VERIFY");
		}
	} catch (error) {
		try {
			restoreLocalValue(
				input.storage,
				LOCAL_DATA_KEYS.receipts,
				previousReceipts,
			);
			restoreLocalValue(
				input.storage,
				LOCAL_DATA_KEYS.household,
				previousHousehold,
			);
			if (
				input.replaceAll ||
				changed.some(
					(receipt) =>
						!input.beforeReceipts.some(
							(original) => original.receiptId === receipt.receiptId,
						),
				)
			) {
				await input.receiptStore.replaceAll(input.beforeReceipts);
				const restored = await input.receiptStore.listAllReceipts();
				if (
					restored.length !== input.beforeReceipts.length ||
					input.beforeReceipts.some(
						(receipt) =>
							JSON.stringify(
								restored.find(
									(candidate) => candidate.receiptId === receipt.receiptId,
								),
							) !== JSON.stringify(normalizeReceiptForStorage(receipt)),
					)
				)
					throw new Error("E-RECEIPT-STORE-ROLLBACK-VERIFY");
			} else
				for (const receipt of changed) {
					const original = input.beforeReceipts.find(
						(original) => original.receiptId === receipt.receiptId,
					);
					if (!original) continue;
					await input.receiptStore.saveReceipt(
						original,
						(normalizeReceiptForStorage(original).items ?? []).map((item) => ({
							receiptId: original.receiptId,
							...item,
						})),
					);
					const restored = (await input.receiptStore.listAllReceipts()).find(
						(receipt) => receipt.receiptId === original.receiptId,
					);
					if (
						JSON.stringify(restored) !==
						JSON.stringify(normalizeReceiptForStorage(original))
					) {
						throw new Error("E-RECEIPT-STORE-ROLLBACK-VERIFY");
					}
				}
		} catch (rollbackError) {
			throw new ReceiptHouseholdPersistError(rollbackError, true);
		}
		throw new ReceiptHouseholdPersistError(error);
	}
}

export function createReceiptStore(): ReceiptStore {
	if (typeof indexedDB === "undefined") return new MemoryReceiptStore();
	return new DexieReceiptStore();
}

export const DEFAULT_SETTINGS = {
	monthStartDay: "1",
	keepImages: "true",
	juiceMuted: "false",
	hapticEnabled: "true",
	statsEnabled: "true",
} as const;

export function todayKst(now = new Date()): PlainDate {
	const shifted = new Date(now.getTime() + 9 * 60 * 60 * 1000);
	return shifted.toISOString().slice(0, 10) as PlainDate;
}

/** #53: one settings owner; no new Dexie version or index. Invalid data is never defaulted away. */
export async function readDayCloses(
	store: Pick<ReceiptStore, "getSetting">,
): Promise<DayClose[]> {
	return parseDayCloses(await store.getSetting(DAY_CLOSES_KEY));
}

export type DayCloseLoadResult =
	| { status: "ready"; closes: DayClose[] }
	| { status: "recovery"; raw: string | null; reason: LocalDataIssueReason };

/** A damaged stamp setting does not make otherwise valid receipts unreadable. */
export async function loadDayCloses(
	store: Pick<ReceiptStore, "getSetting">,
): Promise<DayCloseLoadResult> {
	let raw: string | null;
	try {
		raw = await store.getSetting(DAY_CLOSES_KEY);
	} catch {
		return { status: "recovery", raw: null, reason: "read_error" };
	}
	try {
		return { status: "ready", closes: parseDayCloses(raw) };
	} catch {
		const parsed = raw === null ? { ok: true } : parseJson(raw);
		return {
			status: "recovery",
			raw,
			reason: parsed.ok ? "invalid_schema" : "invalid_json",
		};
	}
}

export const DAY_CLOSE_RECOVERY_PREFIX = "sobidogam:recovery:day-closes:";

export class LocalBackupRollbackError extends Error {
	constructor(readonly cause: unknown) {
		super("E-LOCAL-BACKUP-ROLLBACK");
	}
}

export function listDayCloseRecoveryCopies(storage: LocalStoragePort): {
	storageKey: string;
	createdAt: string;
	raw: string;
}[] {
	const copies = [];
	for (let index = 0; index < storage.length; index += 1) {
		const storageKey = storage.key(index);
		if (!storageKey?.startsWith(DAY_CLOSE_RECOVERY_PREFIX)) continue;
		try {
			const copy = JSON.parse(storage.getItem(storageKey) ?? "null");
			if (
				isObject(copy) &&
				copy.schemaVersion === 1 &&
				copy.sourceKey === DAY_CLOSES_KEY &&
				typeof copy.raw === "string" &&
				typeof copy.createdAt === "string"
			)
				copies.push({ storageKey, createdAt: copy.createdAt, raw: copy.raw });
		} catch {
			// A damaged preservation copy is not a verified recovery export.
		}
	}
	return copies;
}

/** Validate the entire replacement before writing, then preserve damaged bytes first. */
export async function restoreLocalBackup(input: {
	storage: LocalStoragePort;
	receiptStore: ReceiptStore;
	beforeReceipts: Receipt[];
	beforeHousehold: HouseholdConsumptionState;
	backup: {
		receipts: Receipt[];
		household: HouseholdConsumptionState;
		dayCloses?: DayClose[];
	};
}): Promise<DayClose[]> {
	if (!isLocalSnapshot(input.backup.receipts, input.backup.household))
		throw new Error("E-LOCAL-BACKUP-SCHEMA");
	const next = parseDayCloses(
		input.backup.dayCloses === undefined
			? null
			: JSON.stringify(input.backup.dayCloses),
	);
	const before = await input.receiptStore.getSetting(DAY_CLOSES_KEY);
	let damaged = false;
	try {
		parseDayCloses(before);
	} catch {
		damaged = true;
	}
	if (damaged) {
		const key = `${DAY_CLOSE_RECOVERY_PREFIX}${globalThis.crypto.randomUUID()}`;
		const copy = JSON.stringify({
			schemaVersion: 1,
			sourceKey: DAY_CLOSES_KEY,
			createdAt: new Date().toISOString(),
			raw: before,
		});
		input.storage.setItem(key, copy);
		if (input.storage.getItem(key) !== copy)
			throw new Error("E-DAY-CLOSE-RECOVERY-VERIFY");
	}
	if ((await input.receiptStore.getSetting(DAY_CLOSES_KEY)) !== before)
		throw new Error("E-DAY-CLOSE-CHANGED");
	try {
		const encoded = JSON.stringify(next);
		await input.receiptStore.setSetting(DAY_CLOSES_KEY, encoded);
		if ((await input.receiptStore.getSetting(DAY_CLOSES_KEY)) !== encoded)
			throw new Error("E-DAY-CLOSE-VERIFY");
		await persistReceiptAndHouseholdChange({
			...input,
			afterReceipts: input.backup.receipts.map(normalizeReceiptForStorage),
			afterHousehold: normalizeShoppingEntryIds(input.backup.household),
			replaceAll: true,
		});
	} catch (error) {
		try {
			await input.receiptStore.setSetting(DAY_CLOSES_KEY, before ?? "[]");
			if (
				(await input.receiptStore.getSetting(DAY_CLOSES_KEY)) !==
				(before ?? "[]")
			)
				throw new Error("E-DAY-CLOSE-ROLLBACK-VERIFY");
		} catch (rollbackError) {
			throw new LocalBackupRollbackError(rollbackError);
		}
		throw error;
	}
	return next;
}
export async function writeDayCloses(
	store: Pick<ReceiptStore, "getSetting" | "setSetting">,
	next: DayClose[],
): Promise<void> {
	const encoded = JSON.stringify(next);
	parseDayCloses(encoded);
	const before = await store.getSetting(DAY_CLOSES_KEY);
	// Only the explicit backup-recovery owner may replace damaged original bytes.
	parseDayCloses(before);
	try {
		await store.setSetting(DAY_CLOSES_KEY, encoded);
		if ((await store.getSetting(DAY_CLOSES_KEY)) !== encoded)
			throw new Error("E-DAY-CLOSE-VERIFY");
	} catch (error) {
		await store.setSetting(DAY_CLOSES_KEY, before ?? "[]");
		throw error;
	}
}
